import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Collapse, Typography, CircularProgress, List } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { useMeContext } from 'modules/me';
import { useScrollbarStyles } from 'modules/common/styles';
import { useFeatureFlag } from 'modules/common/hooks';
import { SmartDiaryEntry } from './type';
import {
  SmartDiaryList,
  SmartDiaryListItemHeader,
  SmartDiaryListHeader
} from './SmartDiaryList';
import {
  useFormUpdateDirtyState,
  useSmartDiaryAccordianState,
  useSmartDiaryPagination
} from './SmartDiaryList/hooks';
import { useSmartEntriesSelectionState } from './hooks';

type ListStyleProps = {
  hasScroll: boolean;
  isTiwVpatEnabled: boolean;
};

const useStyles = makeStyles((theme) => ({
  diary: {
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
    width: '100%',
    paddingRight: ({ isTiwVpatEnabled }: ListStyleProps) =>
      isTiwVpatEnabled ? theme.spacing(0) : theme.spacing(1)
  },
  headerRoot: {
    paddingRight: ({ hasScroll }: ListStyleProps) =>
      hasScroll ? theme.spacing(0.625) : theme.spacing(0)
  },
  loading: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    padding: theme.spacing(1, 0)
  },
  loadingText: {
    paddingLeft: theme.spacing(1)
  },
  list: {
    padding: theme.spacing(0)
  }
}));

type SmartDiaryListContainerProps = {
  smartEntriesByDay: Record<string, SmartDiaryEntry[]>;
  smartEntries: SmartDiaryEntry[];
  testId: string;
};

const useHasVerticalScroll = (smartEntries: SmartDiaryEntry[]) => {
  const containerRef = useRef<HTMLInputElement>(null);
  const [hasVerticalScroll, setHasVerticalScroll] = useState(false);
  useEffect(() => {
    if (containerRef.current) {
      setHasVerticalScroll(
        containerRef.current.scrollHeight > containerRef.current.offsetHeight
      );
    }
  }, [smartEntries]);
  return { hasVerticalScroll, containerRef };
};

export const SmartDiaryListContainer: React.FC<SmartDiaryListContainerProps> = memo(
  ({
    smartEntriesByDay,
    smartEntries,
    testId
  }: SmartDiaryListContainerProps) => {
    const { containerRef, hasVerticalScroll } = useHasVerticalScroll(
      smartEntries
    );
    const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');
    const classes = useStyles({
      hasScroll: hasVerticalScroll,
      isTiwVpatEnabled
    });
    const classesScrollbar = useScrollbarStyles();

    const { id } = useMeContext() ?? {};
    const expandCollapseAllId = `${id}_sd_expandAll`;
    const {
      onExpandCollapse,
      smartDiaryAccordianState,
      onExpandCollapseAll
    } = useSmartDiaryAccordianState({
      expandCollapseAllId: expandCollapseAllId,
      diariesMap: smartEntriesByDay
    });
    useSmartEntriesSelectionState(smartEntries);
    useFormUpdateDirtyState({ smartEntries });

    const { entriesByDay, hasMore, loadMore } = useSmartDiaryPagination(
      smartEntries,
      expandCollapseAllId
    );

    const [sentryRef] = useInfiniteScroll({
      loading: false,
      hasNextPage: hasMore,
      onLoadMore: loadMore,
      rootMargin: '0px 0px 500px 0px',
      delayInMs: 250
    });

    const entries = useMemo(() => entriesByDay, [entriesByDay]);
    const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

    const listByDay = useMemo(
      () =>
        Object.keys(entries).map((date) => {
          const _smartEntries = entries[date];

          if (!_smartEntries || _smartEntries.length === 0) {
            return false;
          }
          return (
            <div key={date} data-testid={`smart-diary-day-container_${date}`}>
              {isTiwScreenReaderEnabled ? (
                <List className={classes.list}>
                  <SmartDiaryListItemHeader
                    title={date}
                    onExpandCollapse={onExpandCollapse}
                    expanded={smartDiaryAccordianState[date]}
                    smartEntries={smartEntriesByDay[date]}
                  />
                </List>
              ) : (
                <SmartDiaryListItemHeader
                  title={date}
                  onExpandCollapse={onExpandCollapse}
                  expanded={smartDiaryAccordianState[date]}
                  smartEntries={smartEntriesByDay[date]}
                />
              )}
              <Collapse in={smartDiaryAccordianState[date]} timeout="auto">
                <SmartDiaryList smartEntries={_smartEntries} />
              </Collapse>
            </div>
          );
        }),
      [
        classes.list,
        entries,
        isTiwScreenReaderEnabled,
        onExpandCollapse,
        smartDiaryAccordianState,
        smartEntriesByDay
      ]
    );

    return (
      <>
        <SmartDiaryListHeader
          smartEntries={smartEntries}
          onExpandCollapseAll={onExpandCollapseAll}
          expandCollapseAllId={expandCollapseAllId}
          className={classes.headerRoot}
        />
        <div
          data-testid={`smart-diary-list-container_${testId}`}
          className={classnames(classes.diary, classesScrollbar.scrollbar)}
          ref={containerRef}
        >
          {listByDay}
          {hasMore && (
            <div
              className={classes.loading}
              data-testid="smart-diary-inifinite-scroll-loading"
            >
              <CircularProgress size={18} />
              <Typography
                ref={sentryRef}
                variant="body2"
                className={classes.loadingText}
              >
                <FormattedMessage id="smartDiary.loading" />
              </Typography>
            </div>
          )}
        </div>
      </>
    );
  }
);

SmartDiaryListContainer.displayName = 'SmartDiaryListContainer';
